import { useCallback, useMemo } from "react";

import { ErrorResponse, getErrorMessages } from "../../../services";
import { useGetRecurringTransactionsQuery } from "../../../services/recurringTransactions";
import { RecurringTransactionsResponse } from "../../../services/recurringTransactions/types";
import { ALL_VALUE } from "../components/TransactionsFilters/constants";
import { FilterValues } from "../components/TransactionsFilters/types";

interface UseRecurringTransactionsProps {
  accountFilter: FilterValues["account"];
}

export interface UseRecurringTransactionsReturn {
  recurringTransactions: RecurringTransactionsResponse[];
  isLoadingRecurring: boolean;
  recurringError: string | null;
  refetchRecurring: () => Promise<void>;
}

export const useRecurringTransactions = ({
  accountFilter,
}: UseRecurringTransactionsProps): UseRecurringTransactionsReturn => {
  const recurringAccountId = useMemo(() => {
    if (!accountFilter || accountFilter.value === ALL_VALUE) {
      return undefined;
    }
    return Number(accountFilter.value);
  }, [accountFilter]);

  const {
    data: recurringTransactionsResponse = [],
    error,
    isFetching,
    refetch,
  } = useGetRecurringTransactionsQuery(recurringAccountId);

  const recurringTransactions = useMemo(
    () =>
      [...recurringTransactionsResponse].sort(
        (a, b) =>
          new Date(a.nextExecutionDate).getTime() -
          new Date(b.nextExecutionDate).getTime(),
      ),
    [recurringTransactionsResponse],
  );

  const recurringError = useMemo(() => {
    if (!error) {
      return null;
    }

    return (
      getErrorMessages(error as ErrorResponse)[0] ||
      "Failed to load recurring transactions."
    );
  }, [error]);

  const refetchRecurring = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    recurringTransactions,
    isLoadingRecurring: isFetching,
    recurringError,
    refetchRecurring,
  };
};
